import { useLanguage } from '../LanguageContext';
import React, { useState, useEffect, useCallback } from "react";
import { Container, Typography, Tabs, Tab, Button, Snackbar, Alert, Box, Chip } from "@mui/material";
import DataTable from './DataTable';
import BanModal from "./BanModal";
import DetailsModal from "./DetailsModal";
import axiosInstance from "../axiosInstance";

const BannedPlayers = () => {
  const { t, locale } = useLanguage();
  // State for bans
  const [allBans, setAllBans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeBans, setActiveBans] = useState([]);
  const [expiredBans, setExpiredBans] = useState([]);

  // UI states
  const [selectedTab, setSelectedTab] = useState(0);
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedPlayerBans, setSelectedPlayerBans] = useState([]);
  const [selectedPlayerName, setSelectedPlayerName] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [isReissuingBans, setIsReissuingBans] = useState(false);

  const checkCurrentUserRole = async () => {
    try {
      const response = await axiosInstance.get("/api/check-auth", { withCredentials: true });
      setCurrentUserRole(response.data.role || "");
    } catch (error) {
      console.error("❌ Error checking user role:", error);
    }
  };

  const fetchBans = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/api/get_bans");
      let bans = response.data.bans || [];

      bans.sort((a, b) => new Date(b.ban_start) - new Date(a.ban_start));

      const active = bans.filter((ban) => ban.is_active);

      setAllBans(bans);
      setActiveBans(active);
      setExpiredBans(bans.filter(ban => !ban.is_active));
    } catch (error) {
      console.error("❌ Error fetching bans:", error);
      setSnackbar({ open: true, message: t("Failed to fetch bans. Please try again."), severity: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBans();
    checkCurrentUserRole();
  }, [fetchBans]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleOpenBanModal = (player) => {
    setSelectedPlayer(player);
    setShowBanModal(true);
  };

  const handleOpenDetailsModal = (playerIdentity) => {
    const playerBans = allBans.filter((ban) => ban.player_identity === playerIdentity);
    setSelectedPlayerBans(playerBans);
    setSelectedPlayerName(playerBans[0]?.player_name || t("Unknown"));
    setIsDetailsModalOpen(true);
  };

  const handleReissueActiveBans = async () => {
    const targetServerId = window.prompt(t("Target server id to prepare:"), "server2");
    if (!targetServerId) {
      return;
    }

    const confirmed = window.confirm(
      t("Re-issue {count} active ban(s) to {server}?", { count: activeBans.length, server: targetServerId }) + "\n\n" +
      t("This will only send RCON ban commands. It will not lookup/kick players or write new database bans.")
    );

    if (!confirmed) {
      return;
    }

    setIsReissuingBans(true);

    try {
      const response = await axiosInstance.post(
        "/api/reissue_active_bans",
        {
          target_server_id: targetServerId,
          delay_seconds: 1.5,
          dry_run: false,
        },
        { withCredentials: true }
      );

      const data = response.data || {};
      const severity = data.success ? "success" : "warning";
      setSnackbar({
        open: true,
        severity,
        message:
          t("Ban re-issue finished for {server}.", { server: targetServerId }) + " " +
          t("Issued: {issued}, skipped: {skipped}, failed: {failed}.", { issued: data.issued_count || 0, skipped: data.skipped_count || 0, failed: data.failed_count || 0 }),
      });
    } catch (error) {
      console.error("❌ Error re-issuing active bans:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t("Failed to re-issue active bans."),
        severity: "error",
      });
    } finally {
      setIsReissuingBans(false);
    }
  };

  const handleUnbanPlayer = async (playerId, playerName) => {
    try {
      const response = await axiosInstance.post("/api/unban_player", {
        identity: playerId,
        player_name: playerName,
      });

      let snackbarMessage, snackbarSeverity;
      if (response.data.success) {
        // ✅ Unban Success Handling
        const messages = response.data.server_responses || {};
        let successCount = 0;
        let failureCount = 0;
        const successMessages = [];
        const failureMessages = [];

        for (const serverId in messages) {
          if (messages[serverId].toLowerCase().includes("ban removed") || messages[serverId].toLowerCase().includes("success")) {
            successCount++;
            successMessages.push(t("✅ Unbanned from {server}", { server: serverId }));
          } else {
            failureCount++;
            failureMessages.push(t("❌ Failed on {server}: {error}", { server: serverId, error: messages[serverId] }));
          }
        }
        if (successCount > 0 && failureCount === 0) {
          snackbarMessage = t("✅ Unbanned successfully on all servers.");
          snackbarSeverity = 'success';
        } else if (successCount > 0 && failureCount > 0) {
            snackbarMessage = t("⚠️ Unbanned on {success} server(s), but failed on {failed} server(s):\n{details}", { success: successCount, failed: failureCount, details: failureMessages.join("\n") });
            snackbarSeverity = 'warning';
        } else {
          snackbarMessage = t("❌ Failed to unban on all servers:\n{details}", { details: failureMessages.join("\n") });
          snackbarSeverity = 'error';
        }
      } else {
        snackbarMessage = response.data.error || t("❌ Unban request failed.");
        snackbarSeverity = "error";
      }

      setSnackbar({
        open: true,
        message: snackbarMessage,
        severity: snackbarSeverity,
      });

      fetchBans(); // Refresh the list after unban
    } catch (error) {
      console.error("❌ Error unbanning player:", error);
      setSnackbar({ open: true, message: t("Failed to unban the player. Please try again."), severity: "error" });
    }
  };

    const formatBanEndDate = (banEnd) => {
      if (!banEnd) {
        return t("Permanent"); // Handle permanent bans
      }

      // ✅ Convert string to Date object if it's not already
      const endDate = typeof banEnd === 'string' ? new Date(banEnd) : banEnd;

      return endDate.toLocaleString(locale);
    };
  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h4" className="page-header" >{t("Banned Players")}</Typography>

        {currentUserRole === "superadmin" && selectedTab === 0 && (
          <Button
            variant="contained"
            disabled={isReissuingBans || activeBans.length === 0}
            onClick={handleReissueActiveBans}
          >
            {isReissuingBans ? t("Re-issuing...") : t("Re-issue Active Bans")}
          </Button>
        )}
      </Box>

      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          "& .MuiTab-root": { fontWeight: "bold", textTransform: "none" },
        }}
      >
        <Tab label={t("Active bans ({count})", { count: activeBans.length })} />
        <Tab label={t("Expired bans ({count})", { count: expiredBans.length })} />
      </Tabs>

      <DataTable key={selectedTab} label="bans" loading={isLoading} rows={selectedTab === 0 ? activeBans : expiredBans}
        rowKey={ban => `${ban.player_identity}-${ban.ban_start}`} columns={[
          { key: 'player_name', label: t("Player name"), render: ban => <Button onClick={() => handleOpenDetailsModal(ban.player_identity)}>{ban.player_name || t("Unknown")}</Button> },
          { key: 'player_identity', label: t("Player ID"), mono: true },
          { key: 'ban_reason', label: t("Reason") },
          { key: 'admin_name', label: t("Admin") },
          { key: 'is_active', label: t("Status"), value: ban => ban.is_active ? t("Active") : t("Expired"), render: ban => <Chip size="small" variant="outlined" color={ban.is_active ? 'error' : 'default'} label={ban.is_active ? t("Active") : t("Expired")} /> },
          { key: 'ban_start', label: t("Issued time"), value: ban => new Date(ban.ban_start).toLocaleString(locale), sortValue: ban => new Date(ban.ban_start).getTime() },
          { key: 'ban_end', label: t("Ban ends"), value: ban => formatBanEndDate(ban.ban_end), sortValue: ban => ban.ban_end ? new Date(ban.ban_end).getTime() : Number.MAX_SAFE_INTEGER },
          { key: 'actions', label: t("Actions"), searchable: false, render: ban => <div className="row-actions">
            <Button variant="outlined" onClick={() => handleOpenBanModal(ban)}>{t("Edit")}</Button>
            {ban.is_active && <Button variant="outlined" onClick={() => handleUnbanPlayer(ban.player_identity, ban.player_name)}>{t("Unban")}</Button>}
          </div> },
        ]} />

      {/* ✅ Ban Modal */}
      {selectedPlayer && (
        <BanModal
          open={showBanModal}
          onClose={() => setShowBanModal(false)}
          playerData={{ ...selectedPlayer, player_name: selectedPlayer.player_name }}
          mode="edit"
          onSaved={(data) => {
            setSnackbar({ open: true, message: data.message || t("Ban updated."), severity: 'success' });
            fetchBans();
          }}
        />
      )}

      {/* ✅ Details Modal */}
      <DetailsModal
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        playerBans={selectedPlayerBans}
        selectedPlayerName={selectedPlayerName}
        onUnban={handleUnbanPlayer}
      />

      {/* ✅ Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {t(snackbar.message)}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default BannedPlayers;
